import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { get, del } from '@/lib/api'
import { processFileForUpload } from '@/lib/image-utils'

// ==================== Types ====================

export type FileCategory = 'receipt' | 'avatar' | 'knowledge' | 'export'

export interface FileMetadata {
  id: string
  originalName: string
  storagePath: string
  mimeType: string
  size: number
  uploaderId: string
  category: FileCategory
  relatedType: string | null
  relatedId: string | null
  createdAt: string
  deletedAt: string | null
}

export interface FileCategoryConfig {
  allowedTypes: string[]
  maxSize: number
  maxCount: number
  pathPrefix: string
}

export interface UploadOptions {
  category: FileCategory
  relatedType?: string
  relatedId?: string
}

export interface SignedUrlResponse {
  url: string
  expiresAt: string
}

// ==================== Query Keys ====================

export const fileKeys = {
  all: ['files'] as const,
  lists: () => [...fileKeys.all, 'list'] as const,
  list: (relatedType: string, relatedId: string) =>
    [...fileKeys.lists(), relatedType, relatedId] as const,
  detail: (id: string) => [...fileKeys.all, 'detail', id] as const,
  config: () => [...fileKeys.all, 'config'] as const,
}

// ==================== Queries ====================

/**
 * 获取关联文件列表
 */
export function useFilesByRelated(relatedType: string, relatedId: string) {
  return useQuery({
    queryKey: fileKeys.list(relatedType, relatedId),
    queryFn: async () => {
      return get<FileMetadata[]>(`/files?relatedType=${relatedType}&relatedId=${relatedId}`)
    },
    enabled: !!relatedType && !!relatedId,
  })
}

/**
 * 获取文件元数据
 */
export function useFile(id: string) {
  return useQuery({
    queryKey: fileKeys.detail(id),
    queryFn: async () => {
      return get<FileMetadata>(`/files/${id}`)
    },
    enabled: !!id,
  })
}

/**
 * 获取文件配置
 */
export function useFileConfig() {
  return useQuery({
    queryKey: fileKeys.config(),
    queryFn: async () => {
      return get<Record<FileCategory, FileCategoryConfig>>('/files/config')
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  })
}

// ==================== Mutations ====================

/**
 * 上传文件
 */
export function useUploadFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      file,
      options,
    }: {
      file: File
      options: UploadOptions
    }): Promise<FileMetadata> => {
      // 处理文件（图片转 WebP）
      const processed = await processFileForUpload(file)

      // 构建 FormData
      const formData = new FormData()
      formData.append('file', processed.blob, processed.fileName)

      // 构建查询参数
      const params = new URLSearchParams()
      params.set('category', options.category)
      if (options.relatedType) {
        params.set('relatedType', options.relatedType)
      }
      if (options.relatedId) {
        params.set('relatedId', options.relatedId)
      }

      // 发送请求
      const response = await fetch(`/api/v1/files/upload?${params.toString()}`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })

      if (!response.ok) {
        const errorData = (await response.json()) as { error?: { message: string } }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        throw new Error(errorData.error?.message || '上传失败')
      }

      const result = (await response.json()) as { data: FileMetadata }
      return result.data
    },
    onSuccess: (_data, variables) => {
      // 刷新关联文件列表
      if (variables.options.relatedType && variables.options.relatedId) {
        void queryClient.invalidateQueries({
          queryKey: fileKeys.list(variables.options.relatedType, variables.options.relatedId),
        })
      }
    },
  })
}

/**
 * 删除文件
 */
export function useDeleteFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await del(`/files/${id}`)
    },
    onSuccess: () => {
      // 刷新所有文件列表
      void queryClient.invalidateQueries({ queryKey: fileKeys.lists() })
    },
  })
}

/**
 * 获取文件下载链接
 */
export function useGetDownloadUrl() {
  return useMutation({
    mutationFn: async (id: string): Promise<SignedUrlResponse> => {
      return get<SignedUrlResponse>(`/files/${id}/url`)
    },
  })
}

/**
 * 批量上传文件
 */
export function useUploadFiles() {
  const uploadFile = useUploadFile()

  return useMutation({
    mutationFn: async ({
      files,
      options,
    }: {
      files: File[]
      options: UploadOptions
    }): Promise<FileMetadata[]> => {
      const results: FileMetadata[] = []

      for (const file of files) {
        const result = await uploadFile.mutateAsync({ file, options })
        results.push(result)
      }

      return results
    },
  })
}
